import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import './Orders.css';
import { formatINR } from '../../utils/currency';
import { formatShortDate } from '../../utils/date';

const Orders = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (user) {
      fetchOrders();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/ecommerce/orders');
      console.log('Orders API response:', response.data);
      if (response.data.success) {
        const ordersData = response.data.data.orders || [];
        console.log('Orders data:', ordersData);
        setOrders(ordersData);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      pending: '#ffc107',
      confirmed: '#17a2b8',
      processing: '#007bff',
      shipped: '#6f42c1',
      delivered: '#28a745',
      cancelled: '#dc3545',
      refunded: '#6c757d',
      returned: '#fd7e14'
    };
    return statusColors[status] || '#6c757d';
  };

  const getStatusText = (status) => {
    const statusTexts = {
      pending: 'Pending',
      confirmed: 'Confirmed',
      processing: 'Processing',
      shipped: 'Shipped',
      delivered: 'Delivered',
      cancelled: 'Cancelled',
      refunded: 'Refunded',
      returned: 'Returned'
    };
    return statusTexts[status] || status;
  };

  const formatDate = (dateString) => formatShortDate(dateString);

  const filteredOrders = Array.isArray(orders) ? (filter === 'all' 
    ? orders 
    : orders.filter(order => order.status === filter)) : [];

  if (!user) {
    return (
      <div className="orders-container">
        <div className="orders-empty">
          <h2>Please log in to view your orders</h2>
          <Link to="/login" className="btn btn-primary">Login</Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="orders-container">
        <div className="orders-loading">
          <h2>Loading your orders...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="orders-container">
        <div className="orders-error">
          <h2>Error loading orders</h2>
          <p>{error}</p>
          <button onClick={fetchOrders} className="btn btn-primary">Try Again</button>
        </div>
      </div>
    );
  }

  if (!Array.isArray(orders) || orders.length === 0) {
    return (
      <div className="orders-container">
        <div className="orders-empty">
          <h2>No orders found</h2>
          <p>You haven't placed any orders yet.</p>
          <Link to="/ecommerce" className="btn btn-primary">Start Shopping</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-container">
      <div className="orders-header">
        <h1>My Orders</h1>
        <p>Track and manage your orders</p>
      </div>

      <div className="orders-filters">
        <button 
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All Orders ({orders.length})
        </button>
        <button 
          className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
          onClick={() => setFilter('pending')}
        >
          Pending
        </button>
        <button 
          className={`filter-btn ${filter === 'processing' ? 'active' : ''}`}
          onClick={() => setFilter('processing')}
        >
          Processing
        </button>
        <button 
          className={`filter-btn ${filter === 'shipped' ? 'active' : ''}`}
          onClick={() => setFilter('shipped')}
        >
          Shipped
        </button>
        <button 
          className={`filter-btn ${filter === 'delivered' ? 'active' : ''}`}
          onClick={() => setFilter('delivered')}
        >
          Delivered
        </button>
      </div>

      <div className="orders-list">
        {filteredOrders.map((order) => (
          <div key={order._id} className="order-card">
            <div className="order-header">
              <div className="order-info">
                <h3>Order #{order.orderNumber}</h3>
                <p className="order-date">Placed on {formatDate(order.createdAt)}</p>
              </div>
              <div className="order-status">
                <span 
                  className="status-badge"
                  style={{ backgroundColor: getStatusColor(order.status) }}
                >
                  {getStatusText(order.status)}
                </span>
              </div>
            </div>

            <div className="order-items">
              {order.items.map((item, index) => (
                <div key={index} className="order-item">
                  <div className="item-image">
                    <img 
                      src={item.productImage || '/placeholder-product.jpg'} 
                      alt={item.productName}
                    />
                  </div>
                  <div className="item-details">
                    <h4>{item.productName}</h4>
                    <p className="item-code">SKU: {item.productCode}</p>
                    {item.variant && (
                      <p className="item-variant">
                        {item.variant.name}: {item.variant.value}
                      </p>
                    )}
                    <p className="item-quantity">Quantity: {item.quantity}</p>
                  </div>
                  <div className="item-price">
                    <span>{formatINR(item.totalPrice)}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="order-summary">
              <div className="summary-row">
                <span>Subtotal:</span>
                <span>{formatINR(order.subtotal)}</span>
              </div>
              {order.taxAmount > 0 && (
                <div className="summary-row">
                  <span>Tax:</span>
                  <span>{formatINR(order.taxAmount)}</span>
                </div>
              )}
              {order.shippingAmount > 0 && (
                <div className="summary-row">
                  <span>Shipping:</span>
                  <span>{formatINR(order.shippingAmount)}</span>
                </div>
              )}
              {order.discountAmount > 0 && (
                <div className="summary-row discount">
                  <span>Discount:</span>
                  <span>-{formatINR(order.discountAmount)}</span>
                </div>
              )}
              <div className="summary-row total">
                <span>Total:</span>
                <span>{formatINR(order.totalAmount)}</span>
              </div>
            </div>

            <div className="order-actions">
              <Link 
                to={`/ecommerce/orders/${order._id}`} 
                className="btn btn-outline"
              >
                View Details
              </Link>
              {order.status === 'delivered' && (
                <button className="btn btn-primary">
                  Reorder
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Orders;
